/**
 * Centralized theme configuration for route categorization and color mapping
 * This allows consistent color/underline styling across all pages
 */

// Route patterns mapped to theme colors
// IMPORTANT: More specific routes must come BEFORE their parent routes
// This ensures correct color inheritance (e.g., /sports-models/golf before /sports-models)
// All project URLs from projects.js should have matching entries here for consistency
export const routeThemes = {
	// Analytics Engineering (green)
	'/analytics/designing-analytics-schemas': 'bg-badger-green',
	'/analytics': 'bg-badger-green',
	'/duckdb': 'bg-badger-green',
	
	// Data Pipelines (blue)
	'/cricket': 'bg-badger-blue',
	'/data-visualisation/dynamic-d3-components': 'bg-badger-blue',
	'/intersecting-lines': 'bg-badger-blue', // Data visualization - child of data-visualisation
	'/data-visualisation': 'bg-badger-blue',
	
	// Games (purple) - Game simulations
	'/games/snakes-and-ladders': 'bg-badger-purple',
	'/games/nonogram-solver': 'bg-badger-purple',
	'/games': 'bg-badger-purple',

	// Simulations (purple) - Monte Carlo simulations and predictive modeling
	'/sports-models/english-pool-monte-carlo-simulation/league-predictions/custom': 'bg-badger-purple',
	'/sports-models/english-pool-monte-carlo-simulation/league-predictions': 'bg-badger-purple',
	'/sports-models/english-pool-monte-carlo-simulation/player-model': 'bg-badger-purple',
	'/sports-models/english-pool-monte-carlo-simulation/foul-impact': 'bg-badger-purple',
	'/sports-models/english-pool-monte-carlo-simulation/break-efficiency': 'bg-badger-purple',
	'/sports-models/english-pool-monte-carlo-simulation/shot-accuracy': 'bg-badger-purple',
	'/sports-models/english-pool-monte-carlo-simulation/glossary': 'bg-badger-purple',
	'/sports-models/english-pool-monte-carlo-simulation': 'bg-badger-purple',
	'/sports-models/league-schedule-generator': 'bg-badger-purple',
	'/sports-models/sports-ranking-methods-colley-massey-elo': 'bg-badger-purple',
	'/sports-models/monte-carlo-simulation-sports-prediction': 'bg-badger-purple',
	'/sports-models/golf/round-prediction': 'bg-badger-purple',
	'/sports-models/golf': 'bg-badger-purple',
	'/sports-models': 'bg-badger-purple',
	
	// Betting (yellow)
	'/betting/kelly-criterion-bankroll-management': 'bg-badger-yellow',
	'/betting/understand-sports-betting': 'bg-badger-yellow',
	'/betting/sports-betting-bankroll-management-strategies': 'bg-badger-yellow',
	'/betting/how-to-find-value-betting-on-sports': 'bg-badger-yellow',
	'/betting/how-to-maximise-sports-betting-accumulators': 'bg-badger-yellow',
	'/betting': 'bg-badger-yellow',
	
	// Pool (red)
	'/pool/matches': 'bg-badger-red',
	'/pool': 'bg-badger-red',
	
	// Data Pipelines - Additional
	'/steam/games': 'bg-badger-blue',
	'/steam/insights': 'bg-badger-blue',
	'/steam': 'bg-badger-blue',
	
	// Additional routes
	'/about': 'bg-badger-green', // About page - using analytics green as default
	'/playground': 'bg-badger-orange', // Development/testing playground
};

/**
 * Get theme color for a given pathname
 * Matches project config colors for consistency
 * @param {string} pathname - The current pathname (e.g., '/betting/understand-sports-betting')
 * @returns {string} - Color class (e.g., 'bg-badger-yellow')
 */
export const getThemeForPath = (pathname) => {
	// Normalize pathname
	const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
	
	// Check for exact matches first
	if (routeThemes[normalizedPath]) {
		return routeThemes[normalizedPath];
	}
	
	// Check for path prefixes (most specific first - longest paths first)
	// This ensures /sports-models/golf matches before /sports-models
	const sortedRoutes = Object.keys(routeThemes).sort((a, b) => b.length - a.length);
	for (const route of sortedRoutes) {
		if (normalizedPath.startsWith(route)) {
			return routeThemes[route];
		}
	}
	
	// Default to no color if no match
	return null;
};

/**
 * Headline tags configuration (used on homepage)
 */
export const headlineTags = [
	{ text: "analytics engineering", colorClass: "bg-badger-green" },
	{ text: "data pipelines", colorClass: "bg-badger-blue" },
	{ text: "simulations", colorClass: "bg-badger-purple" }
];

